import type {
  ParameterTimeSeriesConfiguration,
  ParametersPropertyKind,
  ParametersUnit,
} from "@/lib/api";

export type ParameterWithUnits = {
  units: ParametersUnit[];
};

/** Minimal shape carrying the physical quantity a parameter belongs to. */
export type ParameterWithPropertyKind = {
  propertyKindId: string;
};

/**
 * Maps a `property_kind_id` to the full unit family (every unit the backend can
 * convert that physical quantity to), sourced from `GET /parameters/property_kinds`.
 */
export type UnitFamilyMap = Map<string, ParametersUnit[]>;

export function buildUnitFamilyMap(
  propertyKinds: Pick<ParametersPropertyKind, "id" | "units">[],
): UnitFamilyMap {
  return new Map(propertyKinds.map((kind) => [kind.id, kind.units]));
}

/**
 * Resolves the unit family for a property kind from the catalog, falling back to
 * the provided units (e.g. a parameter's own `units`) when the catalog has not
 * loaded or the kind is unknown. Keeps the CMS usable before the fetch settles.
 */
export function familyUnitsForKind(
  propertyKindId: string | undefined,
  families: UnitFamilyMap,
  fallbackUnits: ParametersUnit[] = [],
): ParametersUnit[] {
  if (propertyKindId) {
    const family = families.get(propertyKindId);
    if (family && family.length > 0) return family;
  }
  return fallbackUnits;
}

/** Two parameters may share a chart group iff they measure the same quantity. */
export function sharesPropertyKind(
  a: ParameterWithPropertyKind,
  b: ParameterWithPropertyKind,
): boolean {
  return a.propertyKindId === b.propertyKindId;
}

export function resolveUnitFromCatalog(
  units: ParametersUnit[],
  unitCode: string,
): ParametersUnit | undefined {
  return units.find((unit) => unit.code === unitCode);
}

export function resolveFetchUnitForParameter(
  parameterId: string,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): string | undefined {
  for (const config of timeSeriesConfigurations) {
    const inGroup = config.series.some((series) => series.parameter_id === parameterId);
    if (inGroup) return config.unit?.code;
  }
  return undefined;
}

export function configuredTimeSeriesParameterIdSet(
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): Set<string> {
  return new Set(
    timeSeriesConfigurations.flatMap((config) =>
      config.series.map((series) => series.parameter_id),
    ),
  );
}

export function pickAutoUnitCode(
  overlappingUnits: ParametersUnit[],
  preferredUnitCode: string | undefined,
): string | undefined {
  if (overlappingUnits.length === 0) return undefined;
  if (preferredUnitCode) {
    const match = overlappingUnits.find((unit) => unit.code === preferredUnitCode);
    if (match) return match.code;
  }
  return overlappingUnits[0].code;
}
